import React, { useEffect, useState } from 'react';
import { Button, Text, View } from "../../../Components/Themed"
import type { AthleteProps, EventProps, MarketProps, MarketStatsProps, MatchProps, TeamProps, TournamentProps } from '../../../types';
import { MarketComponentApi, MarketComponentHelpers } from '../../api';
import { Alert, FlatList, Platform } from 'react-native';
import { useColors } from '../../../constants/useColors';
import { AthleteImage, TeamImage } from '../../../Components/Jerseys';
import MarketRollbackForm from './MarketRollbackForm';
import MarketResolveForm from './MarketResolveForm';
import moment from 'moment-mini';
import { Icons, Toggle } from '../../../Components';
import OrderList from './OrderList';
import TradeList from './TradeList';

type MarketManagerProps = {
    player_id?:string,
    contest_type:string,
    onViewMatch:(match_id:string) => void
    contest_id:string,
    onViewTrade:(trade_id:string) => void,
    onViewPlayer:(player_id:string) => void,
    onViewOrder:(order_id:string) => void
    onClose:() => void
}

const manage_sections = ['header','total_stats','markets','orders', 'tournament_toggle', 'matches']

const MarketManager = ({ player_id, contest_type, contest_id, onViewMatch, onViewTrade, onViewOrder, onClose, onViewPlayer }:MarketManagerProps) => {
    const Colors = useColors();
    const [ tournament_toggle, setTournamentToggle ] = useState('stats');
    const [ order_list, setOrderList ] = useState<{
        visible: boolean,
        order_ids:string[]
    }>({ visible: false, order_ids: [] });
    const [ rollback, setRollback ] = useState<{
        visible:boolean,
        market?:MarketProps,
        side_type?:string,
        side_id?:string
    }>({
        visible: false
    });

    const [ resolve, setResolve ] = useState<{
        visible:boolean,
        market?:MarketProps,
        side_type?:string,
        side_id?:string
    }>({
        visible: false
    });

    const [ active_market, setActiveMarket ] = useState<MarketProps|undefined>(undefined);
    const [ trades_visible, setTradesVisisble ] = useState(false);
    const [ form_data, setFormData ] = useState<{
        loading: boolean,
        action_loading:boolean,
        markets:MarketProps[],
        market_stats:MarketStatsProps[],
        athletes:AthleteProps[],
        teams:TeamProps[],
        event?:EventProps,
        tournament?:TournamentProps,
        tournament_matches:MatchProps[],
        match?:MatchProps,
    }>({
        loading:false,
        action_loading:false,
        athletes: [],
        teams:[],
        tournament_matches:[],
        markets: [],
        market_stats: []
    });
    const {  market_stats, event, tournament, match, markets, athletes, teams, tournament_matches } = form_data;
    const { closed, total_volume, commission, routing_prem, reconciliation, total_unresolved } = MarketComponentHelpers.getReconFromMarketStats(market_stats);
    const { contest_title } = MarketComponentHelpers.getContestDetails(contest_type, event, tournament, match);
    const supported_markets = MarketComponentHelpers.sortMarketStats(market_stats, MarketComponentHelpers.getSupportedMarkets(contest_type, markets, event, tournament, match));

    const contest_stats = market_stats.filter(s => s.event_type == contest_type && s.event_id == contest_id && s.market_id == active_market?.market_id);
    const order_ids = MarketComponentHelpers.getOrderIdsFromStats(contest_stats);
    const overallActionsAllowed = () => {
        let response = { overall_rollback:false, overall_resolve:false }
        if(!active_market){ return response }
        let contest_stats = market_stats.filter(s => s.event_type == contest_type && s.event_id == contest_id);
        let agg_stats = MarketComponentHelpers.getReconFromMarketStats(contest_stats);
        if(agg_stats.total_paid > 0){ response.overall_rollback = true }
        if(agg_stats.total_unresolved > 0){ response.overall_resolve = true }
        return response
    }

    const { overall_resolve, overall_rollback } = overallActionsAllowed()
    useEffect(() => {
        if(!player_id){ return }
        if(!contest_id){ return }
        getData();
    },[player_id, contest_id]);

    const getContest = async() => {
        let ev:EventProps | undefined = undefined
        let mtch:MatchProps | undefined = undefined
        let tn:TournamentProps | undefined = undefined
        switch(contest_type){
            case 'team':
                let evs = await MarketComponentApi.getEventsByEventIds([contest_id]);
                ev = evs[0]
                break
            case 'match':
                let mtchs = await MarketComponentApi.getMatchesByMatchIds([contest_id]);
                mtch = mtchs[0]
                if(mtch){
                    let mt_tns = await MarketComponentApi.getTournamentsByTournamentIds([contest_id]);
                    tn = mt_tns[0]
                }
                break
            case 'tournament':
                let tns = await MarketComponentApi.getTournamentsByTournamentIds([contest_id]);
                tn = tns[0]
                break
            default: break
        }
        return { event:ev, tournament: tn, match: mtch }
    }

    const getReconColor = (recon:number) => {
        if(!closed){ return Colors.text.warning }
        if(recon < 0){ return Colors.text.error }
        return Colors.text.success
    }

    const getData = async() => {
        MarketComponentApi.setEnvironment();
        setFormData({ ...form_data, loading:true });
        const me = await MarketComponentApi.getMyDetails();
        if(me?.role != 'admin'){ return Alert.alert('Not authorized to view this!') }
        let mk_stats = await MarketComponentApi.getMarketStatsByEvent(contest_id, contest_type);
        let team_ids = mk_stats.filter(s => s.side_type == 'team').map(s => s.side_id);
        let athlete_ids = mk_stats.filter(s => s.side_type == 'athlete').map(s => s.side_id);
        let tourney_toggle = 'stats';
        let contest_resp = await getContest();
        //If there is a tournament, then lets grab the matches!
        let tn_matches:MatchProps[] = []
        if(contest_resp.tournament){
            tn_matches = await MarketComponentApi.getMatchesByTournament(contest_resp.tournament.tournament_id);
            //If we have matches - lets try to default the toggle
            let tournament_stats = mk_stats.filter(s => s.event_type == contest_type && s.event_id == contest_id);
            if(tournament_stats.length == 0){ tourney_toggle = 'matches' }
            //Grab the stats for the matches!!
            let match_stats = await MarketComponentApi.getMarketStatsByEventIds('match', tn_matches.map(m => m.match_id));
            mk_stats = mk_stats.concat(match_stats);
            //Add to the athletes / teams
            tn_matches.map(m => {
                if(m.participant_type == 'team'){ team_ids = team_ids.concat(m.participants) }
                if(m.participant_type == 'athlete'){ athlete_ids = athlete_ids.concat(m.participants) }
            });


        }
        const mkets = await MarketComponentApi.getMarkets();

        let tms = await MarketComponentApi.getTeamsByIds(team_ids);
        let aths = await MarketComponentApi.getAthletesByIds(athlete_ids);
        setFormData({
            ...form_data,
            loading:false,
            markets:mkets,
            teams: tms,
            tournament_matches: tn_matches.filter(m => m.status != 'pending'),
            athletes: aths,
            event: contest_resp.event,
            match: contest_resp.match,
            tournament: contest_resp.tournament,
            market_stats: mk_stats.filter(mk => mk.market_type == 'FOR_MONEY')
        });
        setTournamentToggle(tourney_toggle)
    }

    const handleExpire = async(order_ids:string[]) => {
        if(!active_market){ return }
        if(Platform.OS == 'web'){
            const confirmed = await confirm('Are you sure? This will update the expiration times to now.  It may take up to 1 minute for the orders to be closed and refunded')
            if(!confirmed){ return }
            return await MarketComponentApi.expireOpenOrders(contest_id, contest_type, active_market.market_id, order_ids) 
        } else {
            Alert.alert('Are you sure?', 'This will update the expiration times to now.  It may take up to 1 minute for the orders to be closed and refunded', [
                {
                    text: 'Cancel',
                    style:'cancel',
                    onPress:() => console.log('Done')
                },
                {
                    style: 'destructive',
                    text: 'Confirm',
                    onPress:async() => {
                        await MarketComponentApi.expireOpenOrders(contest_id, contest_type, active_market.market_id, order_ids) 
                    }
                }
            ]);
            return true
        }
    }

    const renderMarketStats = (data:{ item:MarketStatsProps, index:number }) => {
        if(data.item.status == 'cancelled' && data.item.called_amt < 0.01){ return <></> }
        if(data.item.buy_sell_ind == 'sell' && data.item.status != 'approved'){ return <></> }
        const athlete = athletes.find(a => a.athlete_id == data.item.side_id);
        const team = teams.find(t => t.team_id  == data.item.side_id);
        const mk_agg = MarketComponentHelpers.getReconFromMarketStats([data.item]);
        const market = markets.find(m => m.market_id == data.item.market_id);
        if(!market){ return <></> }
        const rollback_allowed = market.level != 'event' && mk_agg.total_paid > 0 ? true : false
        const resolve_allowed = market.level != 'event' && mk_agg.total_unresolved > 0 ? true : false
        return (
            <View type='row' style={{ borderBottomWidth:1, borderColor:Colors.borders.light, padding:5 }}>
                <Button float style={{ padding:10, marginRight:3 }} onPress={() => setOrderList({ visible: true, order_ids: data.item.order_ids })}>
                    <Icons.OrderIcon size={12} color={Colors.text.white}/>
                </Button>
                {data.item.side_type == 'athlete' && athlete ?
                <View>
                    <AthleteImage
                        athlete={athlete}
                        size={32}
                    />
                </View>
                :data.item.side_type == 'team' && team ?
                <View>
                    <TeamImage
                        team={team}
                        size={16}
                    />
                </View>
                :<></>}
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h2'>{data.item.title}</Text>
                    <Text theme='light' size={10} style={{ marginTop:3 }}>{data.item.buy_sell_ind.toUpperCase()}: {data.item.status.toUpperCase()}</Text>
                </View>
                <View style={{ alignItems:'flex-end' }}>
                    <Text color={getReconColor(mk_agg.total_unresolved * -1)}>${mk_agg.total_unresolved.toFixed(2)}</Text>
                    <Text theme='light' textAlign='right'>Unresolved</Text>
                </View>
                {data.item.open_amt > 0 && data.item.status == 'approved' ?
                <View style={{ alignItems:'flex-end', marginLeft:5, paddingLeft:5, borderLeftWidth:1, borderColor:Colors.borders.light }}>
                    <Text theme='description'>${data.item.open_amt.toFixed(2)}</Text>
                    <Text theme='light' textAlign='right'>Open</Text>
                </View>
                :<></>}
                <View nativeID='actions' style={{ marginLeft:5, flexDirection:'row' }}>
                    {data.item.status == 'approved' ?
                    <Button    
                        style={{ padding:10 }}
                        type='error'
                        onPress={() => handleExpire(data.item.order_ids)}>
                            <Text theme='h1' color={Colors.text.white} size={10}>CLEAR</Text>
                    </Button>
                    :<></>}
                    {resolve_allowed ?
                     <Button
                        style={{ padding:10, marginLeft:5 }}
                        type='success'
                        onPress={() => setResolve({ ...resolve, visible: true, side_type: data.item.side_type, side_id: data.item.side_id, market:market })}>
                            <Text theme='h1' color={Colors.text.white} size={10}>RESOLVE</Text>
                    </Button>
                    :<></>}
                    {rollback_allowed ?
                    <Button
                        style={{ padding:10, marginLeft:5 }}
                        backgroundColor={Colors.text.warning}
                        onPress={() => setRollback({ ...rollback, visible:true, side_type: data.item.side_type, side_id:data.item.side_id, market: market })}>
                            <Text theme='h1' color={Colors.text.white} size={10}>ROLLBACK</Text>
                    </Button>
                    :<></>}
                </View>
            </View>
        )
    }

    const renderSupportedMarkets = (data: { item:MarketProps, index:number }) => {
        let mk_stats = market_stats.filter(s => s.market_id == data.item.market_id);
        let mk_agg = MarketComponentHelpers.getReconFromMarketStats(mk_stats);
        const selected = data.item.market_id == active_market?.market_id ? true : false

        return (
            <Button float style={{ width:100, padding:0, margin:4, borderColor:selected?Colors.text.success:Colors.borders.light }}
            onPress={() => {
                if(selected){ setActiveMarket(undefined) }
                else { setActiveMarket(data.item) }
            }}>
                <View type='header' style={{ padding:5, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                    <Text theme='light' size={8}>{data.item.type == 'Stat' ? data.item.stat_label : data.item.type}</Text>
                </View>
                <View type='body' transparent style={{ padding:10, flex:1, justifyContent:'flex-end', alignItems:'flex-end' }}>
                    <Text theme='h1' color={Colors.text.h1}>{mk_agg.total_volume.toFixed(2)}</Text>
                    <Text theme='light' color={Colors.text.h1} size={10}>Volume</Text>
                </View>
                <View type='footer' style={{ padding:5, borderBottomRightRadius:8, borderBottomLeftRadius:8, flexDirection:'row', alignItems:'center' }}>
                    <Text theme='light' style={{ flex:1 }}>Rec:</Text>
                    <Text theme='h1' color={getReconColor(mk_agg.reconciliation)} size={12} textAlign='right'>${mk_agg.reconciliation.toFixed(2)}</Text>
                </View>
            </Button>
        )
    }

    const renderMatches = (data: { item:MatchProps, index: number }) => {
        //const away_athlete = athletes.find(a => a.athlete_id == data.item.participants[0])
        //const home_athlete = athletes.find(a => a.athlete_id == data.item.participants[1])
        //const away_team = teams.find(a => a.team_id == data.item.participants[0])
        //const home_team = teams.find(a => a.team_id == data.item.participants[1])
        const match_stats = market_stats.filter(s => s.event_type == 'match' && s.event_id == data.item.match_id);
        const match_agg = MarketComponentHelpers.getReconFromMarketStats(match_stats);
        return (
            <View type='row' style={{ padding:10, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                <View style={{ flex:1 }}>
                    <Text theme='h2' size={12}>{data.item.match_title}</Text>
                    <Text theme='description' style={{ marginTop:3 }} size={10}>{moment(data.item.scheduled_datetime).format('MM/DD/YY hh:mm a')} - {data.item.status}</Text>
                </View>
                <View style={{ alignItems:'flex-end' }}>
                    <Text color={getReconColor(match_agg.reconciliation)}>${match_agg.reconciliation.toFixed(2)}</Text>
                    <Text theme='light' textAlign='right'>Reconciliation</Text>
                </View>
                <Button
                    style={{ padding:10, marginLeft:5 }}
                    backgroundColor={Colors.text.action}
                    onPress={() => onViewMatch(data.item.match_id)}
                >
                    <Text theme='h1' size={10} color={Colors.text.white}>View Match</Text>
                </Button>
            </View>
        )
    }

    const renderSections = (data: { item:string, index:number }) => {
        switch(data.item){
            case 'header':
                return (
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                        <View transparent style={{ flex:1 }}>
                            <Text theme='h1'>{contest_title}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>Manage Market Data</Text>
                        </View>
                        <Button
                            float
                            style={{ padding:10, borderRadius:100 }}
                            title='X'
                            onPress={() => onClose()}
                        />
                    </View>
                )
            case 'total_stats':
                return (
                    <View type='row' style={{ borderBottomWidth:1, borderColor:Colors.borders.light }}>
                        {closed ?
                        <View style={{ padding:10, flex:1 }}>
                            <Text theme='h1' color={Colors.text.success}>${(commission + routing_prem).toFixed(2)}</Text>
                            <Text theme='light' color={Colors.text.success} style={{ marginTop:2 }} size={8}>{commission.toFixed(2)} + {routing_prem.toFixed(2)}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>Revenue</Text>
                        </View>
                        :
                        <View style={{ padding:10, flex:1 }}>
                            <Text theme='h1' color={Colors.text.warning}>${total_unresolved.toFixed(2)}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>Unresolved</Text>
                        </View>
                        }
                        <View style={{ padding:10, flex:1 }}>
                            <Text theme='h1' color={Colors.text.h1}>${total_volume.toFixed(2)}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>Volume</Text>
                        </View>
                        <View style={{ padding:10, flex:1 }}>
                            <Text theme='h1' color={getReconColor(reconciliation)}>${reconciliation.toFixed(2)}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>Recon</Text>
                        </View>
                        
                    </View>
                )
            case 'markets':
                return (
                    <View transparent style={{ padding:10 }}>
                        <FlatList
                            data={supported_markets}
                            key={`supported_markets`}
                            renderItem={renderSupportedMarkets}
                            keyExtractor={(item) => item.market_id.toString()}
                            horizontal
                        />
                    </View>
                )
            case 'tournament_toggle':
                if(contest_type != 'tournament'){ return <></> }
                if(tournament_matches.length == 0){ return <></> }
                let tourney_stats = market_stats.filter(s => s.event_type == contest_type && s.event_id == contest_id);
                if(tourney_stats.length == 0){ return <></> }
                return (
                    <View style={{ padding:10 }}>
                        <Toggle
                            options={[
                                { key: 'stats', label: 'Stats' },
                                { key: 'matches', label: 'Matches' }
                            ]}
                            onSelectOption={(key) => setTournamentToggle(key)}
                            selected_option={tournament_toggle}
                        />
                    </View>
                )
            case 'orders':
                if(!active_market){ return <></> }
                if(contest_type == 'tournament' && tournament_toggle != 'stats'){ return <></> }
                return (
                    <FlatList
                        data={contest_stats}
                        key={`unresolved_stats_${active_market?.market_id}`}
                        renderItem={renderMarketStats}
                        keyExtractor={(item) => `${item.market_id}_${item.side_id}_${item.side}_${item.buy_sell_ind}_${item.var_1}_${item.status}`}
                    />
                )
            case 'matches':
                if(!active_market){ return <></> }
                if(contest_type != 'tournament'){ return <></> }
                if(tournament_toggle != 'matches'){ return <></> }
                return (
                    <FlatList
                        data={tournament_matches.sort((a,b) => moment(a.scheduled_datetime).unix() - moment(b.scheduled_datetime).unix())}
                        key={`tournament_matches_${active_market?.market_id}`}
                        keyExtractor={(item) => item.match_id.toString()}
                        renderItem={renderMatches}
                    />
                )
            default: return <></>
        }
    }

    return (
        <View style={{ flex:1 }}>
            <View transparent style={{ flex:1 }}>
                <FlatList
                    data={manage_sections}
                    key={'manage_sections'}
                    keyExtractor={(item) => item}
                    renderItem={renderSections}
                />
            </View>
            {active_market ?
            <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                <Button
                    title='View Trades'
                    type='action'
                    style={{ flex:0.5, marginRight:5 }}
                    onPress={() => setTradesVisisble(true)}
                />
                {overall_rollback ?
                <Button
                    title='Rollback'
                    type='error'
                    style={{ flex:1, marginRight:3 }}
                    onPress={() => setRollback({ ...rollback, visible: true, market:active_market })}
                />
                :<></>}
                {overall_resolve ?
                <Button
                    title='Resolve'
                    type='success'
                    style={{ flex:1, marginLeft:3 }}
                    onPress={() => setResolve({ visible: true, market: active_market })}
                />
                :<></>}
            </View>
            :<></>}
            {rollback.visible && rollback.market ?
            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0 }}>
                <MarketRollbackForm
                    contest_title={contest_title}
                    contest_id={contest_id}
                    contest_type={contest_type}
                    market={rollback.market}
                    market_stats={market_stats}
                    side_type={rollback.side_type}
                    onRollback={() => getData()}
                    side_id={rollback.side_id}
                    athletes={athletes}
                    teams={teams}
                    onClose={() => setRollback({ visible: false })}
                />
            </View>
            :<></>}
            {resolve.visible && resolve.market ?
            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0 }}>
                <MarketResolveForm
                    contest_title={contest_title}
                    contest_id={contest_id}
                    contest_type={contest_type}
                    market={resolve.market}
                    market_stats={market_stats}
                    side_type={resolve.side_type}
                    side_id={resolve.side_id}
                    onResolve={() => getData()}
                    athletes={athletes}
                    teams={teams}
                    onClose={() => setResolve({ visible: false })}
                />
            </View>
            :<></>}
            {order_list.visible ?
            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'flex-end' }}>
                <OrderList
                    order_ids={order_list.order_ids}
                    onViewOrder={onViewOrder}
                    onViewPlayer={onViewPlayer}
                    onClose={() => setOrderList({ visible: false, order_ids: [] })}
                />
            </View>
            :<></>}
            {active_market && trades_visible ?
            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0 }}>
                <TradeList
                    contest_id={contest_id}
                    contest_title={contest_title}
                    contest_type={contest_type}
                    order_ids={order_ids}
                    onViewOrder={onViewOrder}
                    onViewTrade={onViewTrade}
                    onViewPlayer={onViewPlayer}
                    market={active_market}
                    onClose={() => setTradesVisisble(false)}
                />
            </View>
            :<></>}
        </View>
    )
}

export default MarketManager